import React, {
    ChangeEvent,
    HTMLAttributes,
    ReactElement,
    useCallback,
    useMemo,
} from "react";
import { RiSubtractLine } from "@remixicon/react";
import classNames from "classnames";
import Toggle, { SIZES, VARIANT } from "./Toggle";
import Styles from "./ToggleList.module.scss";
import Button from "../button/Button";

export interface Option<T> {
    label: string;
    value?: T;
    id: string;
}

export interface ToggleListProps<T> extends Omit<HTMLAttributes<HTMLDivElement>, "options" | "onChange"> {
    options: Option<T>[];
    selectedValues: string[];
    allowSelectAll?: boolean;
    size?: typeof SIZES[number];
    variant?: typeof VARIANT[number];
    onChange: (value: Option<T>[]) => void;
}

const ToggleList = React.forwardRef(<T, >(
    args: ToggleListProps<T>,
    ref: React.ForwardedRef<HTMLDivElement>,
): ReactElement => {
    const {
        options,
        selectedValues,
        onChange,
        className,
        size,
        variant,
        allowSelectAll,
        ...rest
    } = args;

    const selectedSet = new Set(selectedValues);

    const optionMap = useMemo(() => options.toIdMap("id"), [options]);

    const onClick = (event: ChangeEvent<HTMLInputElement>, option: Option<T>) => {
        let newList;
        if (event.target.checked) {
            newList = [...selectedValues, option.id];
        } else {
            newList = selectedValues.filter(id => id !== option.id);
        }

        onChange(newList.map(id => optionMap[id]).filterNull());
    };
    const selectAll = useCallback(() => {
        onChange(options.toIdList("id").map(id => optionMap[id]).filterNull());
    }, [options, onChange]);

    const selectAllOrNone = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        if (event.target.checked) {
            selectAll();
        } else {
            onChange([]);
        }
    }, [selectAll, onChange]);

    const isPartialSelected = selectedSet.size > 0 && selectedSet.size < options.length;
    return (
        <div ref={ref} className={classNames(Styles["toggle-list"], className)} {...rest}>
            {allowSelectAll && (
                isPartialSelected ? (
                    <div className={Styles["toggle-list-item"]}>
                        <span>All</span>
                        <Button
                            size="xsmall"
                            variant="primary"
                            className={classNames(Styles["toggle-list-button"], Styles[`toggle-list-button-${size}`])}
                            prefixIcon={<RiSubtractLine />}
                            onClick={selectAll}
                        />
                    </div>
                ) : (
                    <Toggle
                        text="All"
                        textPlacement="start"
                        checked={selectedSet.size === options.length}
                        labelClassName={Styles["toggle-list-item"]}
                        onChange={selectAllOrNone}
                        size={size}
                        variant={variant}
                    />
                )
            )}

            {options?.map(option => (
                <Toggle
                    key={option.id}
                    text={option.label}
                    textPlacement="start"
                    checked={selectedSet.has(option.id)}
                    labelClassName={Styles["toggle-list-item"]}
                    onChange={event => onClick(event, option)}
                    size={size}
                    variant={variant}
                />
            ))}
        </div>
    );
});

ToggleList.defaultProps = {
    className: "",
    size: "medium",
    variant: "none",
};

export default ToggleList;
